/** @forge-header
 * @id          microstructure
 * @role        Compute orderbook health metrics from raw YES/NO bid arrays
 * @pattern     pure-fn
 * @purity      none
 * @units       cents
 * @money-path  yes
 * @upstream    nbaConsolidated/types:BookLevel
 * @downstream  StreamRift (microstructure panels + useStreamRiftDefense) | G4/refinementPasses | pricing (barrel)
 * @exports     computeBookMetrics:fn | classifyLiquidity:fn | formatSpread:fn | formatImbalance:fn | BookMetrics:interface
 * @test        microstructure.test.ts (18 cases)
 *
 * @invariant Best YES ask = 100 - best NO bid (bid-ask inversion) | Fundamental identity for binary markets. If NO bids are empty, ask is null (not 0, not 100).
 * @invariant Depth in dollars | bidDepthDollars = sum(priceCents/100 * size) YES side, askDepthDollars = sum((100-priceCents)/100 * size) NO side inverted. The /100 converts cents to dollars. Removing it causes 100x overcount.
 */

import type { BookLevel } from '@/types';

export interface BookMetrics {
  /** Spread in cents (best ask - best bid). Null if no 2-sided market. */
  spreadCents: number | null;
  /** Midpoint price in cents. Null if no 2-sided market. */
  midPriceCents: number | null;
  /** Best bid price in cents */
  bestBidCents: number | null;
  /** Best ask price in cents */
  bestAskCents: number | null;
  /** Total bid depth in contracts */
  bidDepthContracts: number;
  /** Total ask depth in contracts (derived from NO bids) */
  askDepthContracts: number;
  /** Total bid depth in dollars */
  bidDepthDollars: number;
  /** Total ask depth in dollars */
  askDepthDollars: number;
  /** Imbalance ratio from -1 (all asks) to +1 (all bids). 0 = balanced. */
  imbalance: number;
  /** Depth ratio: bid_depth / (bid_depth + ask_depth). 0.5 = balanced. */
  depthRatio: number;
  /** Size at top of book (best bid quantity) */
  topBidSize: number;
  /** Size at top of book (best ask quantity) */
  topAskSize: number;
  /** Number of price levels with bids */
  bidLevels: number;
  /** Number of price levels with asks */
  askLevels: number;
  /** Liquidity assessment */
  liquidityLabel: 'HIGH' | 'MEDIUM' | 'LOW' | 'EMPTY';
}

/**
 * Compute orderbook metrics from YES and NO bid arrays.
 *
 * YES bids = bids for YES contracts (descending by price)
 * NO bids = bids for NO contracts (descending by price)
 *
 * Derivation:
 * - Best YES bid = max(yesBids price)
 * - Best YES ask = 100 - max(noBids price) [i.e., derived from NO side]
 * - Spread = best ask - best bid
 */
export function computeBookMetrics(yesBids: BookLevel[], noBids: BookLevel[]): BookMetrics {
  // Best YES bid = highest price in yesBids
  const bestBidLevel = yesBids.length > 0 ? yesBids[0] : null; // assumes descending sort
  const bestBidCents = bestBidLevel?.priceCents ?? null;
  const topBidSize = bestBidLevel?.size ?? 0;

  // @ai-danger: Best YES ask = 100 - highest NO bid price (binary market identity).
  //   If NO side is empty, ask is null — NOT 0 or 100.
  const bestNoBid = noBids.length > 0 ? noBids[0] : null;
  const bestAskCents = bestNoBid ? 100 - bestNoBid.priceCents : null;
  const topAskSize = bestNoBid?.size ?? 0;

  // Spread and mid
  const spreadCents =
    bestBidCents !== null && bestAskCents !== null ? bestAskCents - bestBidCents : null;
  const midPriceCents =
    bestBidCents !== null && bestAskCents !== null
      ? (bestBidCents + bestAskCents) / 2
      : (bestBidCents ?? bestAskCents ?? null);

  // @ai-danger: Depth dollar conversions — priceCents/100 = dollars per contract.
  //   Removing the /100 causes 100x overcount in depth dollars.
  const bidDepthContracts = yesBids.reduce((sum, level) => sum + level.size, 0);
  const bidDepthDollars = yesBids.reduce(
    (sum, level) => sum + (level.priceCents / 100) * level.size,
    0
  );
  const bidLevels = yesBids.length;

  // @ai-note: Ask depth inverts NO bids: (100 - priceCents)/100 * size.
  //   Same inversion as bestAskCents above, applied to full depth.
  const askDepthContracts = noBids.reduce((sum, level) => sum + level.size, 0);
  const askDepthDollars = noBids.reduce(
    (sum, level) => sum + ((100 - level.priceCents) / 100) * level.size,
    0
  );
  const askLevels = noBids.length;

  // Imbalance: (bid - ask) / (bid + ask), clamped to [-1, 1]
  const totalDepth = bidDepthContracts + askDepthContracts;
  const imbalance = totalDepth > 0 ? (bidDepthContracts - askDepthContracts) / totalDepth : 0;

  // Depth ratio: bid / total
  const depthRatio = totalDepth > 0 ? bidDepthContracts / totalDepth : 0.5;

  // Liquidity assessment
  const liquidityLabel = classifyLiquidity(totalDepth, spreadCents);

  return {
    spreadCents,
    midPriceCents,
    bestBidCents,
    bestAskCents,
    bidDepthContracts,
    askDepthContracts,
    bidDepthDollars,
    askDepthDollars,
    imbalance,
    depthRatio,
    topBidSize,
    topAskSize,
    bidLevels,
    askLevels,
    liquidityLabel,
  };
}

/** Classify liquidity based on total depth and spread */
export function classifyLiquidity(
  totalDepthContracts: number,
  spreadCents: number | null
): 'HIGH' | 'MEDIUM' | 'LOW' | 'EMPTY' {
  if (totalDepthContracts === 0) return 'EMPTY';
  if (spreadCents === null) return 'LOW';
  if (totalDepthContracts >= 1000 && spreadCents <= 3) return 'HIGH';
  if (totalDepthContracts >= 200 && spreadCents <= 6) return 'MEDIUM';
  return 'LOW';
}

/** Format spread for display (e.g., "2c" or "—") */
export function formatSpread(spreadCents: number | null): string {
  if (spreadCents === null) return '\u2014';
  return `${spreadCents}c`;
}

/** Format imbalance as a percentage with direction */
export function formatImbalance(imbalance: number): string {
  const pct = Math.abs(imbalance * 100).toFixed(0);
  if (imbalance > 0.05) return `+${pct}% bid`;
  if (imbalance < -0.05) return `\u2212${pct}% ask`;
  return 'balanced';
}

/**
 * @ai-modification-checklist
 *
 * Before modifying this file, verify:
 *
 * 1. BID↔ASK INVERSION — bestAskCents = 100 - bestNoBid.priceCents.
 *    This identity must hold everywhere. askDepthDollars uses same inversion.
 *
 * 2. DEPTH DOLLARS — priceCents/100 * size = dollars. The /100 converts
 *    cents to dollars. Both bid and ask depth use this pattern.
 *
 * 3. LIQUIDITY THRESHOLDS — classifyLiquidity: HIGH >= 1000 contracts +
 *    spread <= 3c, MEDIUM >= 200 + <= 6c, LOW otherwise. Hardcoded.
 *
 * 4. NULL HANDLING — If either side is empty, spread/mid are null.
 *    mid falls back to whichever side exists (bid ?? ask ?? null).
 *
 * 5. TEST FILE — microstructure.test.ts (18 cases).
 *    Run: npm run dashboard:test -- microstructure
 */
